'use client';

import { CarouselBlock } from '@/payload-types';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { ReactNode, useEffect, useState } from 'react';

import Pagination from '@/components/Carousel/pagination';
import CarouselNextButton from './navigation/CarouselNextButton';
import CarouselPrevButton from './navigation/CarouselPrevButton';

interface ICarouselClientProps extends CarouselBlock {
  contents: ReactNode[];
  hasMultipleItems: boolean;
}

const CarouselClient = (props: ICarouselClientProps) => {
  const {
    contents,
    loop,
    autoplay,
    showNavigationButton,
    showPagination,
    hasMultipleItems,
  } = props;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: loop || false }, [
    Autoplay({ playOnInit: false, delay: 5000 }),
  ]);

  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    if (!hasMultipleItems) return;
    const autoplayPlugin = emblaApi?.plugins()?.autoplay;
    if (!autoplayPlugin) return;

    if (autoplay) {
      autoplayPlugin?.play();
    } else {
      autoplayPlugin?.stop();
    }
  }, [autoplay, emblaApi, hasMultipleItems]);

  return (
    <div className="bg-neutral-100 py-smd">
      <div className="container relative overflow-hidden">
        {showNavigationButton && (
          <CarouselPrevButton
            emblaApi={emblaApi}
            setSelectedIndex={setSelectedIndex}
            selectedIndex={selectedIndex}
            loop={loop || false}
          />
        )}
        <div className="relative overflow-hidden rounded-xl" ref={emblaRef}>
          <div className="flex will-change-transform">
            {contents.map((content, index) => (
              <div
                key={index}
                className="text-shadow-light [&_a]:text-shadow-none min-w-0 flex-[0_0_100%] *:h-full cursor-grab active:cursor-grabbing select-none will-change-transform"
              >
                {content}
              </div>
            ))}
          </div>
        </div>
        {showNavigationButton && (
          <CarouselNextButton
            emblaApi={emblaApi}
            setSelectedIndex={setSelectedIndex}
            contents={contents}
            selectedIndex={selectedIndex}
            loop={loop || false}
          />
        )}
      </div>
      {showPagination && (
        <Pagination
          emblaApi={emblaApi}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />
      )}
    </div>
  );
};

export default CarouselClient;
